import React, { useState, useEffect } from "react";
import {
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Container,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Button, Stack } from "react-bootstrap";
import { useLookups } from "../../services/lookups";
import { usePermissions } from "../../services/permissions";
import PermissionSelector from "./CreateGroup/PermissionSelector";
import { AntSelect } from "../../CommonElements/AntSelect";
import { LockIcon } from "../../assets/icons";

const GroupForm = ({
  initialData = null,
  mutation,
  submitButtonText,
}) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      name: "",
      system_id: "",
      permissions: [],
    },
  });

  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const watchedSystemId = watch("system_id");

  const { data: systems } = useLookups();

  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    isFetching: permissionsFetching,
  } = usePermissions({
    value: watchedSystemId,
    enabled: !!watchedSystemId,
    onSuccess: () => {
      // Only reset permissions if this is not initial data load
      if (!initialData) {
        setSelectedPermissions(new Set());
      }
    },
  });

  const loading = permissionsLoading || permissionsFetching;

  // Get mutation hook with setError function
  const { mutate, isPending } = mutation(setError);

  // Effect to populate form with initial data (for update mode)
  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name || "",
        system_id: initialData.system_id || "",
        permissions: initialData.permissions || [],
      });

      // Set selected permissions for update mode
      if (initialData.permissions && initialData.permissions.length > 0) {
        setSelectedPermissions(new Set(initialData.permissions));
      }
    }
  }, [initialData, reset]);

  const handlePermissionChange = (newSelectedPermissions) => {
    setSelectedPermissions(newSelectedPermissions);
    setValue("permissions", Array.from(newSelectedPermissions));

    // Clear error when permissions are selected
    if (newSelectedPermissions.size > 0) {
      clearErrors("permissions");
    }
  };

  const onSubmit = (data) => {
    // Validate permissions
    if (selectedPermissions.size === 0) {
      setError("permissions", {
        type: "required",
        message: t("PERMISSIONS_REQUIRED"),
      });
      return;
    }

    // Clear permissions error if validation passes
    clearErrors("permissions");

    const payload = {
      name: data.name,
      system_id: data.system_id,
      permissions: Array.from(selectedPermissions),
    };

    // If we have initial data, include the ID for update
    if (initialData?.id) {
      payload.id = initialData?.id;
    }

    mutate(payload);
  };

  return (
    <form className="mt-4" onSubmit={handleSubmit(onSubmit)}>
      <Container fluid className="px-0">
        <Row className="mt-3">
          <Col lg={5} xl={4}>
            <div className="card border border-1 groups-list-card">
              <div className="card-body p-3">
                <h3 className="mb-3">{t("GROUP_NAME")}</h3>

                <FormGroup className="mb-3">
                  <Label for="name" className="required">
                    {t("GROUP_NAME")}
                  </Label>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: t("GROUP_NAME_REQUIRED") }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          disabled={isPending}
                          {...field}
                          id="name"
                          type="text"
                          invalid={!!error}
                        />
                        {error && (
                          <div className="invalid-feedback">{error.message}</div>
                        )}
                      </>
                    )}
                  />
                </FormGroup>

                <Stack className="mb-0">
                  <Label for="system_id" className="required">
                    {t("PLATFORM")}
                  </Label>
                  <Controller
                    name="system_id"
                    control={control}
                    rules={{ required: t("PLATFORM_REQUIRED") }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <AntSelect
                          showSearch
                          {...field}
                          id="system_id"
                          placeholder={t("CHOOSE_PLATFORM")}
                          options={systems?.data?.results?.systems?.data?.map((system) => ({
                            value: system?.id,
                            label: system.name,
                          }))}
                          invalid={!!error}
                          disabled={loading || isPending}
                        />
                        {error && (
                          <div className="invalid-feedback">{error.message}</div>
                        )}
                      </>
                    )}
                  />
                </Stack>
              </div>
            </div>
          </Col>

          <Col>
            <div className="card border border-1">
              <div className="card-body p-3">
                <h3 className="mb-3">{t("GROUP_PERMISSIONS")}</h3>

                {watchedSystemId ? (
                  <PermissionSelector
                    systemId={watchedSystemId}
                    permissionsData={permissionsData}
                    loading={loading}
                    selectedPermissions={selectedPermissions}
                    onPermissionChange={handlePermissionChange}
                    error={errors.permissions}
                    control={control}
                    hideLabel
                  />
                ) : (
                  <div className="permissions-empty-state text-center">
                    <div className="permissions-empty-state-icon">
                      <LockIcon size={48} />
                    </div>
                    <h4 className="permissions-empty-state-title">
                      {t("PLATFORM")}
                    </h4>
                    <p className="permissions-empty-state-description">
                      {t("CHOOSE_PLATFORM")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <div className="mt-4">
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending && <Spinner size="sm" />}
            {submitButtonText}
          </Button>
        </div>
      </Container>
    </form>
  );
};
export default GroupForm;

